/**
 * The buy-credits path shown when Velo is out of AI credits.
 *
 * Extracted out of WizardVeloPanel so a host can COMPOSE it into its own
 * quota action (e.g. a column wizard that also needs a "continue in the
 * classic form" button) instead of losing it: `quotaAction` overrides the
 * panel default rather than adding to it, so a host that passes its own
 * action would otherwise silently drop the purchase link.
 *
 * Role gate: POST /billing/purchase accepts company OWNERs only, so everyone
 * else gets the instruction they CAN act on instead of a link that 403s.
 * Rendering nothing for them was the older behaviour and it left the strip's
 * body telling a VIEWER to go buy credits with no way to do it.
 *
 * WHICH company: in event scope the credits are the EVENT's company's (that is
 * who a turn debits), so the owner that can top them up is that company's
 * OWNER — an outside collaborator is told to ask {company}. Company scope
 * keeps the login-company role. Both fail closed while loading, so the
 * purchase link never flashes for someone who may not have it.
 */
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useCompanyRoleString } from "@/hooks/useCompanyRoleString";
import { useAiAssistantOptional } from "@/providers/AiAssistantProvider";
import { useEventCompanyRole } from "@/lib/query/hooks/useEventEntitlements";

export function AiQuotaActions() {
  const t = useTranslations("aiAssistant");
  const scopeEventId = useAiAssistantOptional()?.scopeEventId ?? null;
  const companyRole = useCompanyRoleString();
  const eventCompany = useEventCompanyRole(scopeEventId);
  const isOwner = scopeEventId ? eventCompany.isCompanyOwner : companyRole === "OWNER";
  if (!isOwner) {
    return (
      <p className="body-3-regular font-tertiary">
        {scopeEventId && eventCompany.companyName
          ? t("creditsAskCompany", { company: eventCompany.companyName })
          : t("creditsAskOwner")}
      </p>
    );
  }
  return (
    <Link
      to="/user/subscription/ai-credits"
      className="body-3-medium font-blue hover:underline"
    >
      {t("buyAiCredits")}
    </Link>
  );
}
